/**
 * Flyout size options for controlling panel width.
 *
 * Sizes are mapped to the pattern library's FlyoutV2Size:
 * - sm: Small flyout for quick edits and simple forms
 * - md: Medium flyout for standard forms (default)
 * - lg: Large flyout for content-heavy views
 * - xl: Extra large flyout for complex multi-section forms
 */
export enum FlyoutSize {
  sm = 'sm',
  md = 'md',
  lg = 'lg',
  xl = 'xl',
}

/**
 * Configuration options for opening a flyout panel.
 *
 * @template T - Type of data passed to the flyout component
 *
 * @example
 * ```typescript
 * const options: FlyoutOptions<MyData> = {
 *   size: FlyoutSize.md,
 *   data: { id: 123, mode: 'edit' },
 * };
 * ```
 */
export interface FlyoutOptions<T = unknown> {
  /**
   * Size of the flyout panel.
   * @default FlyoutSize.md
   */
  size?: FlyoutSize;

  /**
   * Data to pass to the flyout component.
   * Accessible via dependency injection in the component.
   */
  data?: T;

  /**
   * Additional CSS class(es) applied to the flyout panel overlay.
   * Use to apply custom width or styling overrides in global styles.
   */
  panelClass?: string | string[];

  /**
   * Explicit width for the flyout panel overlay (e.g. '20rem', '480px').
   * Passed directly to the CDK dialog config — avoids !important CSS overrides.
   */
  width?: string;

  /**
   * Explicit max-width for the flyout panel overlay.
   * Passed directly to the CDK dialog config — avoids !important CSS overrides.
   */
  maxWidth?: string;
}
